import { useMemo, useState, useCallback } from "react";
import { PickupLocationsTable } from "../components/pickup-locations.table";
import { getPickupLocationsExportColumns } from "../lib/export-columns";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import {
  Button,
  FilterBlock,
  useTableExport,
  type FilterStateItem,
  type SortingState,
} from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { usePickupLocationFilters } from "../model/usePickupLocationFilters";
import { useLocationFilterParams } from "../model/use-location-filter-params";
import { useDeepLinkTarget, useFollowDeepLink } from "../model/use-deep-linked-location";
import { usePickupLocationList } from "../model/use-pickup-location-list";
import { PickupLocationModal } from "~/features/pickup-location-modal/ui/pickup-location.modal";
import {
  ContainerGroupModal,
  type ContainerGroupForm,
} from "~/features/pickup-location-modal/components/container-group.modal";
import { useContainerGroupSubmit } from "~/features/pickup-location-modal/hooks/use-container-group-submit";

import { formatPickupLocationToForm } from "../model/formatPickupLocationToForm";
import { flattenLocationsForExport } from "../lib/cascade-rows";
import { useSearchParams } from "react-router-dom";
import { useMediaQuery } from "~/shared/hooks/useMediaQuery";
import { useListCounterpartiesQuery } from "~/shared/api/organization/organization.grpc";
import { useListContainerTypesQuery } from "~/shared/api/container-type/container-type.grpc";
import { useListPickupLocationTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { ConnectProto } from "@racoon/api";
import { assignableLabels } from "~/shared/lib/organization-labels";
import { useIsOwnOrganizationForWrite } from "~/shared/hooks/use-is-own-organization";

/**
 * Keyed by the company, so a switch starts the screen afresh: no query hook can hand
 * back the previous company's `data` while the new company's request is in flight.
 */
export const PickupLocationsPage = () => {
  const { selectedCompanyId } = useSelectedCompany();
  return <PickupLocationsScreen key={selectedCompanyId ?? "none"} />;
};

const PickupLocationsScreen = () => {
  const t = useT();
  const { selectedCompanyId } = useSelectedCompany();
  const [searchParams] = useSearchParams();
  const isMd = useMediaQuery("(min-width: 768px)");
  const orgIds = useMemo(
    () => (selectedCompanyId ? [BigInt(selectedCompanyId)] : []),
    [selectedCompanyId],
  );

  const { data: counterpartiesData } = useListCounterpartiesQuery(
    { organizationId: BigInt(selectedCompanyId ?? 0) },
    { skip: !selectedCompanyId },
  );
  const { data: pickupLocationTypesData } = useListPickupLocationTypesQuery(
    { ownerOrganizationIds: orgIds },
    { skip: !selectedCompanyId },
  );
  const { data: containerTypesData } = useListContainerTypesQuery(
    { ownerOrganizationIds: orgIds },
    { skip: !selectedCompanyId },
  );

  const {
    selectedState,
    states,
    onStateChange,
    onFilterParamsChange,
    onSearchChange,
    searchValue,
    criteria,
    focusLocation,
    clearHighlight,
  } = usePickupLocationFilters();
  const selectedStatus = Number(selectedState.value);

  const highlightLocationId = searchParams.get("highlightLocation");
  const deepLinkTarget = useDeepLinkTarget(selectedCompanyId, highlightLocationId);

  const [sorting, setSorting] = useState<SortingState>([]);

  const list = usePickupLocationList({
    providerId: selectedCompanyId,
    criteria,
    status: selectedStatus,
    extraProviderIds: deepLinkTarget.providerIds,
    extraLocation: deepLinkTarget.full,
    sorting,
  });
  const { routeIndex } = list;

  const filterParams = useLocationFilterParams(
    criteria,
    useMemo(
      () => ({
        pickupLocationTypes: assignableLabels(pickupLocationTypesData?.labels, criteria.typeId).map(
          (label) => ({ id: String(label.id), name: label.name }),
        ),
        customers: (counterpartiesData?.counterparties ?? []).flatMap((c) =>
          c.organization ? [{ id: String(c.organization.id), name: c.organization.name }] : [],
        ),
        containerTypes: (containerTypesData?.containerTypes ?? []).map((type) => ({
          id: String(type.id),
          name: type.name || `#${type.id}`,
        })),
        routeGroups: list.routeGroups.map((group) => ({ id: String(group.id), name: group.name })),
        trucks: list.trucks.map((truck) => ({ id: String(truck.id), name: truck.plateNumber })),
      }),
      [
        criteria.typeId,
        pickupLocationTypesData,
        counterpartiesData,
        containerTypesData,
        list.routeGroups,
        list.trucks,
      ],
    ),
  );

  const statesWithCounts = useMemo(
    () => states.map((state) => ({ ...state, count: list.counts[state.value] })),
    [states, list.counts],
  );

  const deepLink = useFollowDeepLink({
    target: deepLinkTarget,
    // Route filters only: arriving schedule chunks must not re-judge the link.
    routeIndex: list.routesOnlyIndex,
    routeIndexReady: list.routeIndexReady,
    criteria,
    status: selectedStatus,
    focusLocation,
  });
  const { pinned } = deepLink;
  // The list as shown: a pinned deep-linked location first, then the loaded rows.
  const displayedLocations = useMemo(
    () =>
      pinned
        ? [pinned, ...list.locations.filter((location) => location.id !== pinned.id)]
        : list.locations,
    [pinned, list.locations],
  );

  // A filter the user changes releases the pinned location.
  const handleStateChange = (state: FilterStateItem) => {
    deepLink.release();
    onStateChange(state);
  };
  const handleFilterParamsChange = (key: string, value: string | null) => {
    deepLink.release();
    onFilterParamsChange(key, value);
  };
  const handleSearchChange = (value: string) => {
    deepLink.release();
    onSearchChange(value);
  };
  const handleSortingChange = (next: SortingState) => {
    deepLink.release();
    setSorting(next);
  };

  const exportRows = useMemo(
    () => flattenLocationsForExport(displayedLocations, routeIndex),
    [displayedLocations, routeIndex],
  );
  const { handleExportExcel } = useTableExport({
    data: exportRows,
    getData: list.getExportRows,
    columns: getPickupLocationsExportColumns(t),
    reportName: t(Namespace.Routes, "pickupLocations"),
  });

  const [selectedLocationId, setSelectedLocationId] = useState<bigint | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  // Navigation Logic
  const currentIndex = useMemo(() => {
    if (!selectedLocationId) return -1;
    return displayedLocations.findIndex((item) => BigInt(item.id) === selectedLocationId);
  }, [displayedLocations, selectedLocationId]);

  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < displayedLocations.length - 1;

  const handlePrevious = useCallback(() => {
    if (hasPrevious) {
      setSelectedLocationId(BigInt(displayedLocations[currentIndex - 1].id));
    }
  }, [hasPrevious, currentIndex, displayedLocations]);

  const handleNext = useCallback(() => {
    if (hasNext) {
      setSelectedLocationId(BigInt(displayedLocations[currentIndex + 1].id));
    }
  }, [hasNext, currentIndex, displayedLocations]);

  const selectedLocation = useMemo(() => {
    return displayedLocations.find((item) => BigInt(item.id) === selectedLocationId) || null;
  }, [displayedLocations, selectedLocationId]);

  const isOwnForWrite = useIsOwnOrganizationForWrite();
  const modalLocation = useMemo(
    () =>
      selectedLocation
        ? ({
            ...formatPickupLocationToForm(selectedLocation),
            id: Number(selectedLocation.id),
          } as any)
        : undefined,
    [selectedLocation],
  );

  // Click handler for pickup location name
  const handleLocationClick = (location: ConnectProto.PickupLocation) => {
    setSelectedLocationId(BigInt(location.id));
    setEditOpen(true);
    // The link has been followed: drop its param, and only it — the tab and filters
    // it selected stay.
    if (highlightLocationId) clearHighlight();
  };

  // A container-group row opens the same modal the PL card uses; the shared
  // submit hook maps its form back onto the update mutation.
  const [editingCg, setEditingCg] = useState<{
    cg: ConnectProto.ContainerGroup;
    location: ConnectProto.PickupLocation;
  } | null>(null);
  const submitContainerGroup = useContainerGroupSubmit();
  // Stable, since the table's columns are memoized on it. Another provider's site (a zone's)
  // opens its read-only card instead: its group is not ours to edit.
  const openContainerGroup = useCallback(
    (cg: ConnectProto.ContainerGroup, location: ConnectProto.PickupLocation) => {
      if (isOwnForWrite(location.ownerOrganizationId, location.serviceProviderId) === true)
        return setEditingCg({ cg, location });
      setSelectedLocationId(BigInt(location.id));
      setEditOpen(true);
    },
    [isOwnForWrite],
  );
  const handleContainerGroupSubmit = useCallback(
    async (formData: ContainerGroupForm) => {
      if (!editingCg) return;
      await submitContainerGroup({
        formData,
        editingGroup: editingCg.cg,
        locationId: editingCg.location.id,
      });
    },
    [editingCg, submitContainerGroup],
  );

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1 gap-4">
      <FilterBlock
        states={statesWithCounts}
        selectedState={selectedState}
        onStateChange={handleStateChange}
        statesWithCategories
        filterParams={filterParams}
        onFilterParamsChange={handleFilterParamsChange}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        direction="column"
        additionalButtonsPosition={isMd ? "bottom" : "withSearch"}
        additionalButtons={
          <div className="flex flex-nowrap gap-2">
            <PickupLocationModal
              location={modalLocation}
              setLocation={(location) => {
                if (!location) setSelectedLocationId(null);
              }}
              open={editOpen}
              // A site listed through a responsible zone is another provider's, or not yet known: read only
              readOnly={
                !!selectedLocation &&
                isOwnForWrite(
                  selectedLocation.ownerOrganizationId,
                  selectedLocation.serviceProviderId,
                ) !== true
              }
              onOpenChange={(open) => {
                setEditOpen(open);
                if (!open) {
                  setSelectedLocationId(null);
                }
              }}
              hasPrevious={hasPrevious}
              hasNext={hasNext}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
            <Button
              id="pickup-locations-export-button"
              variant="excel"
              size="sm"
              aria-label={t(Namespace.Common, "download")}
              title={t(Namespace.Common, "download")}
              onClick={handleExportExcel}
            />
          </div>
        }
      />

      <PickupLocationsTable
        locations={displayedLocations}
        routeIndex={routeIndex}
        loading={list.loading}
        onEndReached={list.loadNextPage}
        loadingMore={list.loadingMore}
        onLocationClick={handleLocationClick}
        onContainerGroupClick={openContainerGroup}
        highlightLocationId={deepLink.deepLinkedId ?? highlightLocationId}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        sortLocations={list.sortsLocally}
      />

      <ContainerGroupModal
        isOpen={!!editingCg}
        onClose={() => setEditingCg(null)}
        onSubmit={handleContainerGroupSubmit}
        locationId={editingCg ? Number(editingCg.location.id) : undefined}
        editingGroup={editingCg?.cg}
      />
    </div>
  );
};
